/**
 * MetaMask Handler Utility
 * Handles MetaMask detection and errors gracefully
 */

// Check if MetaMask is installed
export const isMetaMaskInstalled = () => {
  return typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
};

// Safely initialize MetaMask handling
export const initMetaMaskHandler = () => {
  // Only proceed if we're in a browser environment
  if (typeof window === 'undefined') return;

  // Add global error handler for MetaMask-related errors
  const originalConsoleError = console.error;
  console.error = function (...args) {
    // Filter out MetaMask-specific errors
    const errorString = args.join(' ');
    if (
      errorString.includes('MetaMask') ||
      errorString.includes('ethereum') ||
      errorString.includes('chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn') ||
      errorString.includes('Failed to connect')
    ) {
      // Silently log to debug if needed
      console.debug('MetaMask error suppressed:', errorString);
      return;
    }
    // Call original console.error for other errors
    originalConsoleError.apply(console, args);
  };

  // Prevent MetaMask from auto-connecting
  if (window.ethereum) {
    // Set autoRefreshOnNetworkChange to false to prevent some errors
    if (window.ethereum.autoRefreshOnNetworkChange !== undefined) {
      window.ethereum.autoRefreshOnNetworkChange = false;
    }

    // Remove any auto-connect attempts
    window.ethereum.removeAllListeners && window.ethereum.removeAllListeners();
  }
};

// Connect to MetaMask only when explicitly requested
export const connectMetaMask = async () => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask browser extension.');
  }

  try {
    const accounts = await window.ethereum.request({
      method: 'eth_requestAccounts'
    });
    return accounts[0];
  } catch (error) {
    console.debug('MetaMask connection error:', error);
    throw new Error('Failed to connect to MetaMask. Please try again.');
  }
};

// Get current connected account (if any)
export const getConnectedAccount = async () => {
  if (!isMetaMaskInstalled()) {
    return null;
  }

  try {
    const accounts = await window.ethereum.request({
      method: 'eth_accounts'
    });
    return accounts[0] || null;
  } catch (error) {
    console.debug('Failed to get MetaMask account:', error);
    return null;
  }
};

export default {
  isMetaMaskInstalled,
  initMetaMaskHandler,
  connectMetaMask,
  getConnectedAccount
};